import { Alert, AlertDescription, AlertTitle } from "@coval/ui/alert"
import { Button } from "@coval/ui/button"
import { Card, CardContent } from "@coval/ui/card"
import { FormField } from "@coval/ui/form-field"
import { Input } from "@coval/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { SettingRow } from "@coval/ui/setting-row"
import { Switch } from "@coval/ui/switch"

import {
  usePaidOverageReview,
  type PaidOverageReviewPort,
} from "../hooks/usePaidOverageReview"
import {
  currencyFractionDigits,
  minorAmountInput,
} from "../utils/overageSettings"

interface Props {
  scopeKey: string
  surface: "signup" | "quick_enablement"
  port: PaidOverageReviewPort
}

function Review({ scopeKey, surface, port }: Props) {
  const editor = usePaidOverageReview(scopeKey, surface, port)
  const review = editor.review
  const pendingPayment =
    editor.phase === "payment-setup" ||
    editor.phase === "payment-authentication"
  return (
    <Card data-testid="paid-overage-review" data-phase={editor.phase}>
      <CardContent className="space-y-4 p-6">
        <h2 className="text-sm font-semibold text-coval-text">
          {surface === "signup"
            ? "Optional paid overages"
            : "Review paid overages"}
        </h2>
        <p className="text-sm text-coval-text-muted">
          Charging and spending enforcement are off. A base subscription can
          continue without paid overages.
        </p>
        {editor.phase === "loading" && (
          <p role="status">Checking approved terms…</p>
        )}
        {editor.phase === "unavailable" && (
          <Alert variant="info">
            <AlertTitle>Paid overages are not available yet</AlertTitle>
            <AlertDescription>
              This screen has not recorded a paid-overage acceptance. Existing
              settings and a saved card do not count as acceptance.
            </AlertDescription>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => void editor.load()}
            >
              Check availability again
            </Button>
          </Alert>
        )}
        {editor.phase === "load-error" && (
          <Alert variant="danger">
            <AlertTitle>Couldn't verify current terms</AlertTitle>
            <AlertDescription>No new decision was submitted.</AlertDescription>
            <Button variant="secondary" onClick={() => void editor.load()}>
              Retry loading terms
            </Button>
          </Alert>
        )}
        {review !== null && (
          <>
            <div className="space-y-2 text-sm text-coval-text">
              <p>Disclosure {review.disclosureVersion}</p>
              <p className="whitespace-pre-wrap">{review.disclosureText}</p>
              <ul className="list-disc pl-5">
                {review.priceLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <p>{review.collectionTiming}</p>
              <p>{review.validityLabel}</p>
            </div>
            <form
              role="presentation"
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault()
                void editor.decide("accept")
              }}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  event.target instanceof HTMLInputElement &&
                  editor.issues.length > 0
                ) {
                  event.preventDefault()
                  editor.revealValidation()
                }
              }}
            >
              <FormField
                label="Overage budget"
                error={editor.attempted ? editor.budgetError : null}
              >
                {(ids) => (
                  <>
                    <Select
                      value={editor.mode}
                      disabled={editor.locked}
                      onValueChange={(value) =>
                        editor.setMode(
                          value === "unlimited" ? "unlimited" : "limited",
                        )
                      }
                    >
                      <SelectTrigger {...ids}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {review.limitedMaximumMinor !== null && (
                          <SelectItem value="limited">
                            Set an explicit limit
                          </SelectItem>
                        )}
                        {review.unlimitedOffered && (
                          <SelectItem value="unlimited">
                            Unlimited, under the approved terms
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {editor.mode === "limited" && (
                      <Input
                        data-testid="paid-overage-budget"
                        aria-label={`Budget in ${review.currency}`}
                        value={editor.amount}
                        disabled={editor.locked}
                        inputMode="decimal"
                        onChange={(event) =>
                          editor.setAmount(event.target.value)
                        }
                        aria-invalid={
                          editor.attempted && editor.budgetError !== null
                        }
                        aria-describedby={ids["aria-describedby"]}
                      />
                    )}
                    {review.limitedMaximumMinor !== null &&
                      currencyFractionDigits(review.currency) !== null && (
                        <p className="text-sm text-coval-text-muted">
                          Maximum approved budget:{" "}
                          {minorAmountInput(
                            review.limitedMaximumMinor,
                            review.currency,
                          )}{" "}
                          {review.currency}. Zero is allowed.
                        </p>
                      )}
                  </>
                )}
              </FormField>
              <SettingRow
                label="I accept these exact financial terms"
                tooltip="Acceptance is separate from your base subscription and takes effect prospectively only after server confirmation."
              >
                <Switch
                  data-testid="paid-overage-confirm"
                  aria-label="I accept these exact financial terms"
                  checked={editor.confirmed}
                  disabled={editor.locked}
                  onCheckedChange={editor.setConfirmed}
                />
              </SettingRow>
              {editor.attempted && !editor.confirmed && (
                <p role="alert" className="text-sm text-coval-text-muted">
                  Confirm the displayed financial terms.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="paid-overage-accept"
                  type="submit"
                  disabled={editor.locked}
                >
                  {editor.phase === "saving"
                    ? "Recording decision…"
                    : "Accept paid-overage terms"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  data-testid="paid-overage-decline"
                  disabled={!editor.canDecline}
                  onClick={() => void editor.decide("decline")}
                >
                  Decline paid overages
                </Button>
              </div>
            </form>
          </>
        )}
        {editor.phase === "uncertain" && (
          <Alert variant="danger">
            <AlertTitle>Decision outcome is unknown</AlertTitle>
            <AlertDescription>
              Retry the identical decision before making another choice. No
              acceptance is assumed.
            </AlertDescription>
            <Button
              data-testid="paid-overage-retry"
              variant="secondary"
              onClick={() => void editor.retry()}
            >
              Retry same decision
            </Button>
            <Button
              data-testid="paid-overage-recheck"
              variant="secondary"
              onClick={() => void editor.recheck()}
            >
              Check recorded decision
            </Button>
          </Alert>
        )}
        {(editor.phase === "unverified" ||
          editor.phase === "checking-decision") && (
          <Alert variant="danger">
            <AlertTitle>Couldn't verify the recorded decision</AlertTitle>
            <AlertDescription>
              The original decision is unresolved. Check its retained receipt
              before making another choice. If it still cannot be verified,
              contact Coval; reloading current terms does not resolve it.
            </AlertDescription>
            <Button
              variant="secondary"
              data-testid="paid-overage-recheck"
              disabled={editor.phase === "checking-decision"}
              onClick={() => void editor.recheck()}
            >
              {editor.phase === "checking-decision"
                ? "Checking recorded decision…"
                : "Check recorded decision"}
            </Button>
          </Alert>
        )}
        {(editor.phase === "conflict" || editor.phase === "rejected") && (
          <Alert variant="danger">
            <AlertTitle>Current terms or settings changed</AlertTitle>
            <AlertDescription>
              Review the latest offer before submitting another decision.
            </AlertDescription>
            <Button variant="secondary" onClick={() => void editor.load()}>
              Reload current terms
            </Button>
          </Alert>
        )}
        {pendingPayment && (
          <Alert variant="info">
            <AlertTitle>Payment setup needs attention</AlertTitle>
            <AlertDescription>
              The decision is pending and does not authorize paid usage.
              Complete payment setup through the existing billing flow, then
              reload and explicitly confirm the current terms.
            </AlertDescription>
            <Button
              variant="secondary"
              data-testid="paid-overage-payment-recheck"
              onClick={() => void editor.load()}
            >
              Recheck after payment setup
            </Button>
          </Alert>
        )}
        {editor.phase === "accepted" && (
          <Alert variant="info">
            <AlertTitle>Financial acceptance recorded</AlertTitle>
            <AlertDescription>
              Reference: {editor.reference}. Charging and enforcement remain
              off.
            </AlertDescription>
          </Alert>
        )}
        {editor.phase === "declined" && (
          <p role="status">
            Paid overages declined. Your base subscription remains available.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export function PaidOverageReviewPanel(props: Props) {
  return <Review key={props.scopeKey} {...props} />
}
